import notification from 'antd/lib/notification';
import { useEffect, useState } from 'react';

import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import { downloadFilesApi } from '@/integrations/index.api';
import { handleError } from '@/services/handleError';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  getAttachedFilesByReqId,
  setShouldUpdateFiles
} from '@/store/financingStep.slice';
import { setPageTitle } from '@/store/newOrder.slice';
import { getResponsesByRequestId } from '@/store/offerStep.slice';
import type {
  BankPayloadType,
  FileType,
  TUploadAdditionalFilesDto
} from '@/types/uploadFiles';

import { getVehicleFormKey } from '../../../helpers/helpers';
import useAutoCredRequestId from '../../../hooks/useAutoCredRequestId';
import {
  AltynBank,
  BccBank,
  BerekeBank,
  EurasBank,
  EurasBankV2,
  ForteBank,
  FreedomBank,
  HalykBank,
  SapaBank,
  ShinhanBank,
  SolvaBank
} from '../../features';

export type SendDocumentsHandler<T extends object = BankPayloadType> = (
  uploadedFiles: Record<string, FileType & { re_upload?: boolean }>,
  options?: {
    additionalData?: T;
    fileUuidsToDownloadOnEmpty?: Record<string, string | undefined>;
  }
) => Promise<boolean>;

const CurrentBankView: React.FC = () => {
  const dispatch = useAppDispatch();

  const { userInfo } = useAppSelector((state) => state.userInfo);
  const { selectedBank } = useAppSelector((state) => state.financingStep);
  const { clientInfo } = useAppSelector((state) => state.newOrder);
  const { shouldUpdateFiles } = useAppSelector((state) => state.financingStep);

  const { requestId } = useAutoCredRequestId();

  const [requestLoading, setRequestLoading] = useState(false);

  const sendDocumentsHandler: SendDocumentsHandler = async (
    uploadedFiles,
    options
  ) => {
    if (!userInfo) return false;

    setRequestLoading(true);
    try {
      const { additionalData, fileUuidsToDownloadOnEmpty } = options || {};
      const computedUploadedFiles = { ...uploadedFiles };

      if (fileUuidsToDownloadOnEmpty) {
        for (const fileType in fileUuidsToDownloadOnEmpty) {
          const fileUuid = fileUuidsToDownloadOnEmpty[fileType];
          if (!computedUploadedFiles[fileType] && fileUuid) {
            try {
              const file = await downloadFilesApi.downloadBankFile(fileUuid);
              computedUploadedFiles[fileType] = {
                base64: file.base64,
                name: file.name,
                size: 0
              };
            } catch (error) {
              handleError(error);
              return false;
            }
          }
        }
      }

      const dataArray: TUploadAdditionalFilesDto[] = Object.keys(
        computedUploadedFiles
      )
        .filter((key) => !!computedUploadedFiles[key]?.base64)
        .map((key) => {
          const item: TUploadAdditionalFilesDto = {
            bank_id: selectedBank?.bank.code.toString() || '',
            base64: computedUploadedFiles[key]?.base64 || '',
            iin: clientInfo?.iin || '',
            name: computedUploadedFiles[key]?.name || '',
            type: key,
            request_uuid: requestId,
            response_uuid: selectedBank?.id || '',
            bank_payload: additionalData || null,
            reUpload: computedUploadedFiles[key]?.re_upload
          };
          return item;
        });

      if (dataArray.length === 0) {
        notification.warning({
          title: 'Нет документов для отправки'
        });
        return false;
      }

      await autoCreditApi.uploadAdditionalFiles(dataArray);
      await dispatch(getAttachedFilesByReqId(requestId));
      if (selectedBank?.bank.name === 'eu_bank_v2') {
        await dispatch(getResponsesByRequestId({ reqId: requestId }));
      }

      notification.success({
        title: 'Файлы отправлены',
        description: 'Прикрепленные файлы были успешно отправлены',
        duration: 5
      });
      return true;
    } catch (error) {
      handleError(error, {
        customTitle: 'Ошибка при отправке файлов'
      });
      return false;
    } finally {
      setRequestLoading(false);

      // Очищаем данные по транспортному средству
      sessionStorage.removeItem(getVehicleFormKey(userInfo.uuid, requestId));
    }
  };

  const BANKS_VIEWS: Record<string, React.ReactNode> = {
    altyn: <AltynBank />,
    bcc: (
      <BccBank
        isSubmitLoading={requestLoading}
        onSubmit={sendDocumentsHandler}
      />
    ),
    bereke: <BerekeBank />,
    eu_bank: (
      <EurasBank
        isSubmitLoading={requestLoading}
        onSubmit={sendDocumentsHandler}
      />
    ),
    eu_bank_v2: (
      <EurasBankV2
        isSubmitLoading={requestLoading}
        onSubmit={sendDocumentsHandler}
      />
    ),
    forte: (
      <ForteBank
        isSubmitLoading={requestLoading}
        onSubmit={sendDocumentsHandler}
      />
    ),
    ffb: <FreedomBank />,
    halyk: (
      <HalykBank
        isSubmitLoading={requestLoading}
        onSubmit={sendDocumentsHandler}
      />
    ),
    shinhan: <ShinhanBank />,
    sapa: <SapaBank />,
    solva: (
      <SolvaBank
        isSubmitLoading={requestLoading}
        onSubmit={sendDocumentsHandler}
      />
    )
  };

  useEffect(() => {
    dispatch(setShouldUpdateFiles(true));
    dispatch(setPageTitle('Финансирование'));
  }, []);

  useEffect(() => {
    if (shouldUpdateFiles) {
      dispatch(getAttachedFilesByReqId(requestId));
      dispatch(setShouldUpdateFiles(false));
    }
  }, [shouldUpdateFiles]);

  return BANKS_VIEWS[selectedBank?.bank.name || ''];
};

export default CurrentBankView;
